import React, { useEffect, useState } from 'react';
import Button from '../ui/Button';
import FileUploadManager from './FileUploadManager';
import { Plus, Trash2, Eye, EyeOff, Globe2, ExternalLink } from 'lucide-react';
import {
    addOtherMaterial as appendOtherMaterial,
    normalizeMaterials,
    removeOtherMaterial as deleteOtherMaterial,
    updateOtherMaterial,
} from '../../utils/materials';
import {
    clearPatternDraft,
    loadPatternDraft,
    savePatternDraft,
} from '../../utils/patternDraft';
import {
    DIFFICULTY_LEVELS,
    YARN_WEIGHTS,
    getDifficultyLevel,
    normalizeDifficulty,
} from '../../constants/patternOptions';
import { getPatternNeedles, getPatternYarnWeights } from '../../utils/patternFields';
import { buildPatternPreview, savePatternPreview } from '../../utils/patternPreview';

const CATEGORIES = ['Baby', 'Kids', 'Women', 'Men', 'Accessories'];
const LANGUAGES = ['Spanish', 'English', 'French', 'German', 'Italian'];

export default function ProductForm({
    initialData,
    onSubmit,
    onCancel,
    isLoading,
    draftKey,
    saveProgress,
}) {
    const storage = typeof window !== 'undefined' ? window.localStorage : null;
    const [recoveredDraft] = useState(() => loadPatternDraft(storage, draftKey));
    const data = recoveredDraft?.formData || initialData || {};
    const initialYarnWeights = getPatternYarnWeights(data);
    const initialNeedles = getPatternNeedles(data);
    const initialMaterials = normalizeMaterials(data.materials);
    const [formData, setFormData] = useState({
        title: data.title || '',
        slug: data.slug || '',
        description: data.description || '',
        price: data.price || '',
        previous_price: data.previous_price || '',
        status: data.status === 'published' ? 'published' : 'draft',
        pdf_version: data.pdf_version || '1.0',
        pdf_date: data.pdf_date || '',
        changelog: Array.isArray(data.changelog) ? data.changelog : [],
        difficulty_level: normalizeDifficulty(data.difficulty_level || data.skill_level),
        construction: data.construction || '',
        yarn_weights: initialYarnWeights.length > 0 ? initialYarnWeights : [YARN_WEIGHTS[0]],
        yarn_weight: initialYarnWeights[0] || data.yarn_weight || YARN_WEIGHTS[0],
        needles: initialNeedles.length > 0 ? initialNeedles : [''],
        languages: (data.languages && data.languages.length > 0)
            ? data.languages.map(l => l === 'Español' ? 'Spanish' : l)
            : [LANGUAGES[1]], // Default to English (index 1)
        categories: data.categories || [],
        size_tag: data.size_tag || '',
        materials: initialMaterials,
        video_url: data.video_url || '',
        images: data.images || [],
        main_image: data.main_image || '',
        pdf_files: data.pdf_files || []
    });

    const [images, setImages] = useState(data.images || []);
    const [mainImage, setMainImage] = useState(data.main_image || '');
    const [pdfFiles, setPdfFiles] = useState(data.pdf_files || []);
    const [draftSavedAt, setDraftSavedAt] = useState(recoveredDraft?.savedAt || null);
    const [previewError, setPreviewError] = useState('');

    useEffect(() => {
        const saved = savePatternDraft(storage, draftKey, {
            formData,
            images,
            mainImage,
            pdfFiles,
        });

        if (saved) setDraftSavedAt(new Date().toISOString());
    }, [draftKey, formData, images, mainImage, pdfFiles, storage]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            // Auto-generate slug from title if slug is empty or matches previous title slug
            if (name === 'title' && (!prev.slug || prev.slug === prev.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''))) {
                newData.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            }
            return newData;
        });
    };

    const handleCategoryToggle = (cat) => {
        setFormData(prev => {
            const cats = prev.categories.includes(cat)
                ? prev.categories.filter(c => c !== cat)
                : [...prev.categories, cat];
            return { ...prev, categories: cats };
        });
    };

    const handleLanguageToggle = (lang) => {
        setFormData(prev => {
            const langs = prev.languages.includes(lang)
                ? prev.languages.filter(l => l !== lang)
                : [...prev.languages, lang];
            // When removing a language, also remove associated PDF if any
            if (prev.languages.includes(lang)) {
                setPdfFiles(current => current.filter(p => p.language !== lang));
            }
            return { ...prev, languages: langs };
        });
    };

    const handleYarnWeightToggle = (weight) => {
        setFormData(prev => {
            const selected = prev.yarn_weights.includes(weight);
            const yarnWeights = selected
                ? prev.yarn_weights.filter(item => item !== weight)
                : [...prev.yarn_weights, weight];

            if (yarnWeights.length === 0) return prev;
            return { ...prev, yarn_weights: yarnWeights, yarn_weight: yarnWeights[0] };
        });
    };

    const handleNeedleChange = (index, value) => {
        setFormData(prev => {
            const needles = prev.needles.map((needle, itemIndex) => (
                itemIndex === index ? value : needle
            ));
            return {
                ...prev,
                needles,
                materials: { ...prev.materials, needles: needles.filter(Boolean).join(', ') }
            };
        });
    };

    const addNeedle = () => {
        setFormData(prev => ({ ...prev, needles: [...prev.needles, ''] }));
    };

    const removeNeedle = (index) => {
        setFormData(prev => {
            const needles = prev.needles.filter((_, itemIndex) => itemIndex !== index);
            return {
                ...prev,
                needles,
                materials: { ...prev.materials, needles: needles.filter(Boolean).join(', ') }
            };
        });
    };

    const handleMaterialChange = (field, value, index = null) => {
        setFormData(prev => {
            if (index !== null) {
                return {
                    ...prev,
                    materials: updateOtherMaterial(prev.materials, index, value)
                };
            }
            return {
                ...prev,
                materials: { ...normalizeMaterials(prev.materials), [field]: value }
            };
        });
    };

    const addOtherMaterial = () => {
        setFormData(prev => ({
            ...prev,
            materials: appendOtherMaterial(prev.materials)
        }));
    };

    const handleRemoveOtherMaterial = (index) => {
        setFormData(prev => ({
            ...prev,
            materials: deleteOtherMaterial(prev.materials, index)
        }));
    };

    const addChangelogEntry = () => {
        setFormData(prev => ({
            ...prev,
            changelog: [
                ...prev.changelog,
                { version: prev.pdf_version, date: prev.pdf_date, notes: '' }
            ]
        }));
    };

    const updateChangelogEntry = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            changelog: prev.changelog.map((entry, itemIndex) => (
                itemIndex === index ? { ...entry, [field]: value } : entry
            ))
        }));
    };

    const removeChangelogEntry = (index) => {
        setFormData(prev => ({
            ...prev,
            changelog: prev.changelog.filter((_, itemIndex) => itemIndex !== index)
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const needles = formData.needles.map(item => item.trim()).filter(Boolean);
        onSubmit({
            ...formData,
            needles,
            materials: { ...formData.materials, needles: needles.join(', ') },
            images,
            main_image: mainImage || images[0], // Default to first image if none selected
            pdf_files: pdfFiles
        });
    };

    const handlePreview = () => {
        const pattern = buildPatternPreview(formData, images, mainImage);
        const key = savePatternPreview(storage, pattern);

        if (!key) {
            setPreviewError('The website preview could not be opened. Please check your browser storage settings.');
            return;
        }

        setPreviewError('');
        const previewUrl = new URL('/admin/patterns/preview', window.location.origin);
        previewUrl.searchParams.set('key', key);
        window.open(previewUrl.toString(), '_blank', 'noopener,noreferrer');
    };

    const selectedDifficulty = getDifficultyLevel(formData.difficulty_level);

    const handleCancel = () => {
        clearPatternDraft(storage, draftKey);
        onCancel();
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-8 bg-white p-8 rounded-xl shadow-sm border border-gray-100">
            {recoveredDraft && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900" role="status">
                    <p className="font-semibold">Local draft recovered</p>
                    <p className="mt-1 text-blue-700">
                        Your unsaved fields from {new Date(recoveredDraft.savedAt).toLocaleString()} have been restored.
                    </p>
                    {recoveredDraft.attachmentNames?.length > 0 && (
                        <p className="mt-2 text-amber-700">
                            Reattach these local files before saving: {recoveredDraft.attachmentNames.join(', ')}.
                        </p>
                    )}
                </div>
            )}
            <div className="flex flex-col gap-5 pb-6 border-b border-gray-100 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Visibility Status</span>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, status: 'draft' }))}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 transition-all duration-300 ${formData.status === 'draft'
                                ? 'bg-amber-50 border-amber-500 text-amber-700 shadow-sm shadow-amber-200/50 scale-105'
                                : 'bg-white border-gray-100 text-gray-400 hover:border-amber-200 hover:text-amber-500'
                                }`}
                        >
                            <EyeOff size={18} className={formData.status === 'draft' ? 'animate-pulse' : ''} />
                            <span className="font-semibold text-sm">Draft</span>
                        </button>

                        <div className="w-8 h-[2px] bg-gray-100 rounded-full" />

                        <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, status: 'published' }))}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl border-2 transition-all duration-300 ${formData.status === 'published'
                                ? 'bg-emerald-50 border-emerald-500 text-emerald-700 shadow-sm shadow-emerald-200/50 scale-105'
                                : 'bg-white border-gray-100 text-gray-400 hover:border-emerald-200 hover:text-emerald-500'
                                }`}
                        >
                            <Globe2 size={18} className={formData.status === 'published' ? 'animate-bounce-subtle' : ''} />
                            <span className="font-semibold text-sm">Published</span>
                        </button>
                    </div>
                </div>

                <div className="flex flex-col items-start gap-2 sm:items-end">
                    <p className="text-xs text-gray-400 max-w-[200px] italic">
                        {formData.status === 'published' && "Visible to all customers in the shop."}
                        {formData.status === 'draft' && "Work in progress and hidden from customers."}
                    </p>
                    <button
                        type="button"
                        onClick={handlePreview}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 transition-colors hover:text-blue-900 hover:underline"
                    >
                        <Eye size={17} />
                        Preview on website
                        <ExternalLink size={14} />
                    </button>
                    {previewError && <p className="max-w-sm text-xs text-red-600" role="alert">{previewError}</p>}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column: Basic Info */}
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Product Title</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="e.g.: Luxury Knit Snood"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Slug (URL)</label>
                            <input
                                type="text"
                                name="slug"
                                value={formData.slug}
                                onChange={handleChange}
                                placeholder="luxury-knit-snood"
                                className="w-full px-4 py-2 rounded-lg border border-gray-100 bg-gray-50 text-gray-600 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="4"
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Construction</label>
                        <input
                            type="text"
                            name="construction"
                            value={formData.construction}
                            onChange={handleChange}
                            placeholder="e.g.: Top-down, seamless raglan"
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                        />
                        <p className="mt-1 text-xs text-gray-500">Describe how the piece is assembled and knitted.</p>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Price (€)</label>
                            <input
                                type="number"
                                name="price"
                                value={formData.price}
                                onChange={handleChange}
                                step="0.01"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Previous Price</label>
                            <input
                                type="number"
                                name="previous_price"
                                value={formData.previous_price}
                                onChange={handleChange}
                                step="0.01"
                                placeholder="e.g.: 10.00"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Size / Tag</label>
                            <input
                                type="text"
                                name="size_tag"
                                value={formData.size_tag}
                                onChange={handleChange}
                                placeholder="S-M-L or One Size"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                            />
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Difficulty Level</label>
                            <select
                                name="difficulty_level"
                                value={formData.difficulty_level}
                                onChange={handleChange}
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                            >
                                {DIFFICULTY_LEVELS.map(level => (
                                    <option key={level.value} value={level.value}>{level.label}</option>
                                ))}
                            </select>
                            <p className="mt-2 text-xs leading-relaxed text-gray-500">{selectedDifficulty.description}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Yarn Weights</label>
                            <p className="mb-2 text-xs text-gray-500">Select every yarn weight supported by the pattern.</p>
                            <div className="flex flex-wrap gap-2">
                                {YARN_WEIGHTS.map(weight => (
                                    <button
                                        key={weight}
                                        type="button"
                                        onClick={() => handleYarnWeightToggle(weight)}
                                        className={`rounded-full border px-3 py-1 text-sm transition-colors ${formData.yarn_weights.includes(weight)
                                            ? 'border-primary bg-primary text-white'
                                            : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-primary'
                                            }`}
                                    >
                                        {weight}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Section / Categories</label>
                        <div className="flex flex-wrap gap-2">
                            {CATEGORIES.map(cat => (
                                <button
                                    key={cat}
                                    type="button"
                                    onClick={() => handleCategoryToggle(cat)}
                                    className={`px-3 py-1 rounded-full text-sm transition-colors ${formData.categories.includes(cat)
                                        ? 'bg-primary text-white border-primary'
                                        : 'bg-gray-50 text-gray-600 border border-gray-200 hover:border-primary'
                                        }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Selected Languages</label>
                        <p className="text-xs text-gray-500 mb-2">Select the languages to enable uploading their corresponding PDFs.</p>
                        <div className="flex flex-wrap gap-2">
                            {LANGUAGES.map(lang => (
                                <button
                                    key={lang}
                                    type="button"
                                    onClick={() => handleLanguageToggle(lang)}
                                    className={`px-3 py-1 rounded-full text-sm transition-colors ${formData.languages.includes(lang)
                                        ? 'bg-secondary text-white border-secondary'
                                        : 'bg-gray-50 text-gray-600 border border-gray-200 hover:border-secondary'
                                        }`}
                                >
                                    {lang}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Column: Files & Materials */}
                <div className="space-y-6">
                    <FileUploadManager
                        initialImages={images}
                        initialMainImage={mainImage}
                        initialPdfs={pdfFiles}
                        selectedLanguages={formData.languages}
                        onImagesChange={setImages}
                        onMainImageChange={setMainImage}
                        onPdfsChange={setPdfFiles}
                    />

                    <div className="space-y-4 rounded-xl border border-gray-200 bg-gray-50/50 p-4">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-700">PDF release details</h3>
                            <p className="mt-1 text-xs text-gray-500">Track which pattern revision customers receive.</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="mb-1 block text-xs text-gray-500">Version</label>
                                <input
                                    type="text"
                                    name="pdf_version"
                                    value={formData.pdf_version}
                                    onChange={handleChange}
                                    placeholder="1.0"
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:ring-2 focus:ring-primary"
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-xs text-gray-500">Release date</label>
                                <input
                                    type="date"
                                    name="pdf_date"
                                    value={formData.pdf_date}
                                    onChange={handleChange}
                                    className="w-full rounded-lg border border-gray-300 px-3 py-2 outline-none focus:ring-2 focus:ring-primary"
                                />
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center justify-between gap-3">
                                <label className="text-xs font-medium text-gray-600">Changelog</label>
                                <button
                                    type="button"
                                    onClick={addChangelogEntry}
                                    className="flex items-center text-xs font-medium text-primary hover:underline"
                                >
                                    <Plus size={14} className="mr-1" /> Add release note
                                </button>
                            </div>
                            {formData.changelog.map((entry, index) => (
                                <div key={index} className="space-y-2 rounded-lg border border-gray-200 bg-white p-3">
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={entry.version || ''}
                                            onChange={(event) => updateChangelogEntry(index, 'version', event.target.value)}
                                            aria-label={`Changelog version ${index + 1}`}
                                            placeholder="Version"
                                            className="w-24 rounded-md border border-gray-200 px-2 py-1 text-sm"
                                        />
                                        <input
                                            type="date"
                                            value={entry.date || ''}
                                            onChange={(event) => updateChangelogEntry(index, 'date', event.target.value)}
                                            aria-label={`Changelog date ${index + 1}`}
                                            className="flex-1 rounded-md border border-gray-200 px-2 py-1 text-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeChangelogEntry(index)}
                                            aria-label={`Remove changelog entry ${index + 1}`}
                                            className="rounded-md p-1.5 text-red-500 hover:bg-red-50"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                    <textarea
                                        value={entry.notes || ''}
                                        onChange={(event) => updateChangelogEntry(index, 'notes', event.target.value)}
                                        aria-label={`Changelog notes ${index + 1}`}
                                        placeholder="What changed in this PDF?"
                                        rows="2"
                                        className="w-full rounded-md border border-gray-200 px-2 py-1 text-sm"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-gray-700">Required Materials</h3>
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">Suggested Yarn</label>
                            <input
                                type="text"
                                name="materials_yarn"
                                value={formData.materials.yarn}
                                onChange={(e) => handleMaterialChange('yarn', e.target.value)}
                                placeholder="e.g.: 3 hanks of Merino Wool"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="block text-xs text-gray-500">Needles</label>
                            {formData.needles.map((needle, index) => (
                                <div key={index} className="flex gap-2">
                                    <input
                                        type="text"
                                        value={needle}
                                        onChange={(e) => handleNeedleChange(index, e.target.value)}
                                        placeholder="e.g.: 4.5 mm circular needles"
                                        className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeNeedle(index)}
                                        aria-label={`Remove needle ${index + 1}`}
                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={addNeedle}
                                className="flex items-center text-primary text-sm font-medium hover:underline"
                            >
                                <Plus size={16} className="mr-1" /> Add another needle
                            </button>
                        </div>
                        <div className="space-y-2">
                            <label className="block text-xs text-gray-500">Other Materials</label>
                            {formData.materials.other.map((item, index) => (
                                <div key={index} className="flex gap-2">
                                    <input
                                        type="text"
                                        value={item}
                                        onChange={(e) => handleMaterialChange('other', e.target.value, index)}
                                        className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveOtherMaterial(index)}
                                        aria-label={`Remove material ${index + 1}`}
                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={addOtherMaterial}
                                className="flex items-center text-primary text-sm font-medium hover:underline"
                            >
                                <Plus size={16} className="mr-1" /> Add another material
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Video Link (Optional)</label>
                        <input
                            type="url"
                            name="video_url"
                            value={formData.video_url}
                            onChange={handleChange}
                            placeholder="https://youtube.com/..."
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                        />
                    </div>
                </div>
            </div>

            <div className="pt-6 border-t border-gray-100">
                {isLoading && saveProgress && (
                    <div className="mb-5" role="status" aria-live="polite">
                        <div className="mb-2 flex items-center justify-between gap-4 text-sm">
                            <span className="font-medium text-gray-700">{saveProgress.message}</span>
                            <span className="tabular-nums text-gray-500">{saveProgress.percent}%</span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                            <div
                                className="h-full rounded-full bg-primary transition-all duration-300"
                                style={{ width: `${saveProgress.percent}%` }}
                            />
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-between gap-4">
                    <p className="text-xs text-gray-400" aria-live="polite">
                        {draftSavedAt ? 'Draft saved locally' : 'Local draft pending'}
                    </p>
                    <div className="flex space-x-4">
                <Button variant="secondary" onClick={handleCancel} type="button" disabled={isLoading}>
                    Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                    {isLoading ? 'Saving...' : 'Save Product'}
                </Button>
                    </div>
                </div>
            </div>
        </form>
    );
}
